import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loginUser, getStudentDetails } from "../../lib/AuthApi";
import PasswordInput from "./components/PasswordInput";
import { useAuth } from "../../context/AuthProvider";
import Logo from "../../components/common/Logo";
import { getSubscriptions } from "../../lib/Subscription";
import {
    PlayIcon,
    CircleHelp,
    ShoppingCart,
    Tv,
} from "lucide-react";
import "./LoginPage.css";

const LoginPage = () => {
    const navigate = useNavigate();
    const { setUserData } = useAuth();

    const [form, setForm] = useState({
        username: "",
        password: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // =========================================================
    // API / LOGIN LOGIC - KEPT UNCHANGED
    // =========================================================
    const handleLogin = async (e) => {
        e.preventDefault();

        if (!form.username.trim() || !form.password.trim()) {
            setError("Username and password required");
            return;
        }

        try {
            setLoading(true);
            setError("");

            // ================= LOGIN API =================
            const res = await loginUser({
                username: form.username,
                password: form.password,
            });

            console.log("Login Response:", res);

            // ================= VALIDATION =================
            if (!res?.data?.isSuccess) {
                setError(res?.message || "Login failed");
                return;
            }

            // ================= LOGIN RESULT =================
            const result = res?.data?.result;

            // ================= BASIC USER =================
            const basicUser = {
                token: result?.token,
                userID: result?.userID,
                uuid: result?.uuid,
                sessionId: result?.sessionId,
                role: result?.roleName,
            };

            // ================= SAVE BASIC USER FIRST =================
            localStorage.setItem("user", JSON.stringify(basicUser));
            setUserData(basicUser);

            // ================= STUDENT DETAILS =================
            const studentRes = await getStudentDetails();
            console.log("Student Details:", studentRes);

            const studentData = studentRes?.data?.[0] || {};

            // ================= FINAL USER =================
            let finalUserData = {
                ...basicUser,
                ...studentData,
            };



            // ================= ROLE =================
            const role = finalUserData?.role?.toLowerCase();

            // =====================================================
            // STUDENT
            // =====================================================
            if (role === "student") {
                // ================= CHECK SUBSCRIPTION =================
                // const subRes = await getSubscriptions(finalUserData.uuid, finalUserData.schoolID);

                const subRes = await getSubscriptions(
                    finalUserData.UserID,
                    finalUserData.SchoolId
                );

                console.log("Subscription Response:", subRes);

                // ================= ADD SUBSCRIPTION =================
                finalUserData = {
                    ...finalUserData,
                    hasSubscription: subRes?.success || false,
                    subscriptionData: subRes?.data || null,
                };

                // ================= SAVE USER =================
                localStorage.setItem(
                    "user",
                    JSON.stringify(finalUserData)
                );

                setUserData(finalUserData);

                // ================= REDIRECT =================
                if (subRes?.success) {
                    navigate("/live-classes");
                } else {
                    navigate("/subscription");
                }
            }

            // =====================================================
            // TEACHER / ADMIN
            // =====================================================
            else if (role === "teacher" || role === "admin") {
                finalUserData = {
                    ...finalUserData,
                    hasSubscription: true,
                };

                localStorage.setItem(
                    "user",
                    JSON.stringify(finalUserData)
                );

                setUserData(finalUserData);

                navigate("/live-classes");
            }

            // =====================================================
            // INVALID ROLE
            // =====================================================
            else {
                setError("Invalid user role");
            }
        } catch (err) {
            console.log(err);

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Invalid credentials"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <title>Login | InstaED</title>

            <main className="instaed-login-page">
                <div className="instaed-login-shell">
                    {/* =================================================
                        HEADER
                    ================================================= */}
                    <header className="instaed-login-header">
                        <Link
                            to="/"
                            className="instaed-login-logo"
                            aria-label="InstaED Tuitions home"
                        >
                            <Logo />
                        </Link>
                    </header>

                    {/* =================================================
                        MAIN CONTENT
                    ================================================= */}
                    <section className="instaed-login-layout">
                        {/* =================================================
                            LEFT COLUMN
                        ================================================= */}
                        <div className="instaed-login-left">
                            <div className="instaed-class-card">
                                <div className="instaed-class-card-top">
                                    <h2>Classes 6–10</h2>
                                    <span>Enrolling</span>
                                </div>

                                <div className="instaed-class-details">
                                    <p>
                                        <span className="instaed-detail-icon">◷</span>
                                        4:00–6:00 PM | 2 hours a day
                                    </p>
                                    <p>
                                        <span className="instaed-detail-icon">▦</span>
                                        3 days a week — MWF or TTS
                                    </p>
                                    <p>
                                        <span className="instaed-detail-icon">◫</span>
                                        Mathematics and Science
                                    </p>
                                    <p>
                                        <span className="instaed-detail-icon">♟</span>
                                        30 students per batch, hard cap
                                    </p>
                                </div>
                            </div>

                            <div className="instaed-before-card">
                                <h3>Before you sign in</h3>

                                <Link
                                    to="/demo-session"
                                    className="instaed-wide-link"
                                >
                                    <PlayIcon size={15} strokeWidth={2.4} />
                                    <span>View sample class</span>
                                </Link>

                                <Link
                                    to="/howtouse"
                                    className="instaed-wide-link"
                                >
                                    <CircleHelp size={15} strokeWidth={2.4} />
                                    <span>How it works</span>
                                </Link>

                                <Link
                                    to="/subscription"
                                    className="instaed-wide-link"
                                >
                                    <ShoppingCart size={15} strokeWidth={2.4} />
                                    <span>Buy or renew subscription</span>
                                </Link>

                                <Link
                                    to="/"
                                    className="instaed-wide-link"
                                >
                                    <Tv size={15} strokeWidth={2.4} />
                                    <span>
                                        Display on your Smart TV or desktop
                                    </span>
                                </Link>
                            </div>
                        </div>

                        {/* =================================================
                            RIGHT LOGIN CARD
                        ================================================= */}
                        <div className="instaed-login-card">
                            <div className="instaed-login-card-header">
                                <h1>Login</h1>

                                <Link to="/signup">
                                    New user? Sign up
                                </Link>
                            </div>

                            <form
                                onSubmit={handleLogin}
                                className="instaed-login-form"
                            >
                                <div className="instaed-field">
                                    <label htmlFor="username">
                                        Username, email or mobile
                                    </label>

                                    <input
                                        id="username"
                                        type="text"
                                        name="username"
                                        autoComplete="username"
                                        placeholder="Enter user name"
                                        value={form.username}
                                        onChange={(e) =>
                                            handleChange(
                                                "username",
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div className="instaed-field">
                                    <label htmlFor="password">
                                        Password
                                    </label>

                                    <PasswordInput
                                        value={form.password}
                                        onChange={(e) =>
                                            handleChange(
                                                "password",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Enter password"
                                        className="instaed-password-input"
                                    />
                                </div>

                                <div className="instaed-login-options">
                                    <label className="instaed-remember">
                                        <input
                                            type="checkbox"
                                            defaultChecked
                                        />
                                        <span>Stay signed in</span>
                                    </label>

                                    <Link to="/forget-password">
                                        Forgot password?
                                    </Link>
                                </div>

                                <div className="instaed-login-buttons">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="instaed-signin-button"
                                    >
                                        {loading ? "Logging in..." : "Sign in"}
                                    </button>

                                    <button
                                        type="button"
                                        className="instaed-demo-button"
                                        onClick={() =>
                                            setForm({
                                                username: "demo",
                                                password: "demo123",
                                            })
                                        }
                                    >
                                        Fill demo details
                                    </button>
                                </div>

                                <div className="instaed-login-divider" />

                                <Link
                                    to="/login-with-otp"
                                    className="instaed-secondary-link"
                                >
                                    🔒 <span>Login via OTP</span>
                                </Link>

                                <Link
                                    to="/teacher-login"
                                    className="instaed-secondary-link"
                                >
                                    <span>I'm a teacher</span>
                                </Link>

                                <div className="instaed-login-error">
                                    {error && (
                                        <p>{error}</p>
                                    )}
                                </div>
                            </form>
                        </div>
                    </section>

                    {/* =================================================
                        FOOTER
                    ================================================= */}
                    <footer className="instaed-login-footer">
                        <Link to="/terms-condition">
                            Terms of use
                        </Link>

                        <Link to="/policy">
                            Privacy policy
                        </Link>

                        <span>© 2026</span>
                    </footer>
                </div>
            </main>
        </>
    );
};

export default LoginPage;